/**
 * V2 source path: customer/midas/components/v2/ChatUserMessageV2.tsx
 * V1 source: shared/chatBot/components/ChatUserMessage.tsx
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 1
 *
 * Copied from V1 verbatim:
 *   - blob URL handling (`isBlobUrl`)
 *   - bot-proxy URL detection + `resolveImageProxyUrl`
 *   - authenticated proxy fetch with bearer token → blob URL → revoke
 *     on cleanup (MIDAS-BUG-001-P2 image persistence)
 *   - MIDAS-BUG-003 silent collapse on image load failure
 *   - aspect-ratio reservation when width/height known on `user_image`
 *   - legacy `[image]` literal text → empty string normalization
 *   - sendFailed visual indicator (red border + tooltip)
 *
 * Visual layer rewritten in Tailwind: right-aligned warm-stone bubble
 * `#E8DFD2` (Round 4 spec), Sora 15px graphite, mono timestamp below-right
 * (no Fraunces). Phone: bubble max-width 290px (74% of 390px viewport).
 */
import React, { useEffect, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";

import accountContext from "../../../../account/accountContext";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ChatMessage } from "../../../../shared/chatBot/models/chatBotModels";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

// ═══════════════════════════════════════════════════════════════
//  URL helpers — copied verbatim from V1 ChatUserMessage.tsx
// ═══════════════════════════════════════════════════════════════

/** True for in-session optimistic blob: URLs (created via URL.createObjectURL) */
const isBlobUrl = (url: string): boolean => url.startsWith("blob:");

/** True for bot-hosted image URLs that require authenticated proxy fetch. */
const isBotProxyPath = (url: string): boolean =>
  url.startsWith("/api/v1/image/") || url.includes("/api/v1/image/");

/** Resolve a bot image URL to our server-side proxy endpoint. */
const resolveImageProxyUrl = (imageUrl: string): string => {
  const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
  const marker = "/api/v1/image/";
  const markerIdx = imageUrl.indexOf(marker);
  const stripped =
    markerIdx !== -1
      ? imageUrl.slice(markerIdx + marker.length)
      : imageUrl.replace(/^\/+/, "");
  return `${baseUrl}/v1/bot/image/${stripped}`;
};

interface ChatUserMessageV2Props {
  message: ChatMessage;
  showTimestamp?: boolean;
  isPhone?: boolean;
  /** Called with the renderable image URL + filename when the user clicks the image */
  onImageClick?: (imageUrl: string, imageName: string) => void;
}

const ChatUserMessageV2: React.FC<ChatUserMessageV2Props> = ({
  message,
  showTimestamp = false,
  isPhone = false,
  onImageClick,
}) => {
  const { translate } = useTranslation("chatBot");

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Reserve space using width/height on the user_image artifact (CLS guard)
  const userImageArtifact = message.artifacts?.find(
    (a) => a.type === "user_image"
  );
  const imgWidth = (userImageArtifact?.data as any)?.width as
    | number
    | undefined;
  const imgHeight = (userImageArtifact?.data as any)?.height as
    | number
    | undefined;

  const rawText = message.text ?? "";
  const displayText = rawText === "[image]" ? "" : rawText;
  const hasText = displayText.trim().length > 0;

  const sourceUrl = message.imageUrl || "";
  const hasImageUrl = sourceUrl.length > 0;

  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    hasImageUrl && isBlobUrl(sourceUrl) ? sourceUrl : null
  );
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasImageUrl) {
      setResolvedUrl(null);
      return;
    }
    if (isBlobUrl(sourceUrl)) {
      setResolvedUrl(sourceUrl);
      return;
    }
    if (!isBotProxyPath(sourceUrl)) {
      setResolvedUrl(sourceUrl);
      return;
    }

    let cancelled = false;
    const proxyUrl = resolveImageProxyUrl(sourceUrl);
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(proxyUrl, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setResolvedUrl(url);
      })
      .catch(() => {
        // MIDAS-BUG-003: silently collapse — bubble renders empty.
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUrl]);

  const hasImage = hasImageUrl;

  const handleImageClick = () => {
    if (resolvedUrl && onImageClick) {
      onImageClick(resolvedUrl, message.imageName || "image");
    }
  };

  const bubbleMaxWidth = isPhone ? "max-w-[290px]" : "max-w-[540px]";
  const bubblePad = hasImage && !hasText ? "p-1.5" : "px-4 py-3";

  return (
    <div className="flex w-full flex-col items-end animate-in fade-in slide-in-from-bottom-1 duration-200">
      <div
        className={`relative flex flex-col ${bubbleMaxWidth} ${bubblePad} rounded-[14px] rounded-br-[4px] bg-midas-warm-stone text-[15px] leading-[1.5] text-midas-graphite ${
          message.sendFailed
            ? "border-l-[3px] border-red-500"
            : ""
        }`}
        style={{
          fontFamily: "'Sora', sans-serif",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          gap: hasImage && hasText ? "8px" : 0,
        }}
      >
        {/* Image thumbnail */}
        {hasImage && (
          <div
            onClick={resolvedUrl ? handleImageClick : undefined}
            className={`flex w-full max-w-[240px] items-center justify-center overflow-hidden ${
              hasText ? "rounded-lg" : "rounded-[10px]"
            } ${
              onImageClick && resolvedUrl
                ? "cursor-pointer transition-opacity hover:opacity-85"
                : ""
            }`}
            style={{
              aspectRatio:
                !resolvedUrl && imgWidth && imgHeight
                  ? `${imgWidth} / ${imgHeight}`
                  : undefined,
              backgroundColor: !resolvedUrl
                ? "rgba(0,0,0,0.04)"
                : "transparent",
              minHeight: !resolvedUrl ? "80px" : undefined,
            }}
          >
            {resolvedUrl ? (
              <img
                src={resolvedUrl}
                alt={message.imageName || translate("midas.userImage.uploadLabel")}
                loading="lazy"
                decoding="async"
                width={imgWidth}
                height={imgHeight}
                onError={() => setResolvedUrl(null)}
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "cover",
                  display: "block",
                  borderRadius: hasText ? "8px" : "10px",
                }}
              />
            ) : null}
          </div>
        )}

        {/* Text content */}
        {hasText && <span>{displayText}</span>}

        {message.sendFailed && (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <span className="absolute bottom-1 right-1 inline-flex">
                <AlertCircle
                  className="h-3.5 w-3.5 text-red-400"
                  strokeWidth={2}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <span className="font-mono text-[11px]">
                {translate("sendError")}
              </span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {showTimestamp && (
        <span className="mr-1 mt-1 font-mono text-[10px] font-normal text-midas-muted-stone">
          {formatTime(message.timestamp)}
        </span>
      )}
    </div>
  );
};

export default ChatUserMessageV2;
